// File: src/components/onboarding/NavCoachmark.jsx
import React, { useEffect, useState } from 'react';
import './NavCoachmark.css';

const STORAGE_KEY = 'baobrain_nav_coachmark_seen';

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

/**
 * NavCoachmark
 *
 * Single-tooltip coachmark that points at the sidebar nav. Designed to
 * appear ONCE for users coming out of WelcomeHub via the "Show me around"
 * button. Persists dismissal in localStorage so it never re-appears.
 *
 * NOT a multi-step product tour. Just one tip. One click to dismiss.
 *
 * Props:
 *   show       — controlled visibility from parent (e.g. set true after user
 *                clicks "Show me around" in WelcomeHub)
 *   onDismiss  — called when user closes
 *
 * Usage in App.jsx (or wherever Sidebar is mounted):
 *   const [showCoach, setShowCoach] = useState(false);
 *   <Sidebar />
 *   <NavCoachmark show={showCoach} onDismiss={() => setShowCoach(false)} />
 */
export default function NavCoachmark({ show, onDismiss }) {
  // Hide forever once seen
  const [seen, setSeen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const visible = show && !seen;

  // Auto-position next to the sidebar nav once visible
  const [position, setPosition] = useState({ top: 120, left: 280 });

  useEffect(() => {
    if (!visible) return;

    const updatePosition = () => {
      // Try to anchor to the sidebar nav
      const sidebar = document.querySelector('.navigation-sidebar');
      if (sidebar) {
        const rect = sidebar.getBoundingClientRect();
        // Position to the right of the sidebar, vertically near the top of nav items
        setPosition({
          top: rect.top + 140,
          left: rect.right + 16,
        });
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [visible]);

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      /* ignore */
    }
    setSeen(true);
    if (onDismiss) onDismiss();
  };

  if (!visible) return null;

  return (
    <div
      className="bao-coachmark"
      style={{ top: position.top, left: position.left }}
      role="dialog"
      aria-label="Navigation tip"
    >
      <div className="bao-coachmark-arrow" />
      <button
        className="bao-coachmark-close"
        onClick={handleDismiss}
        aria-label="Dismiss"
        type="button"
      >
        <XIcon />
      </button>
      <p className="bao-coachmark-title">Find your way around</p>
      <p className="bao-coachmark-text">
        Your insights are organized into 3 areas:{' '}
        <strong>who buys</strong>, <strong>where they drop off</strong>, and{' '}
        <strong>what's growing</strong>. Click anything when you're ready.
      </p>
      <button
        className="bao-coachmark-btn"
        onClick={handleDismiss}
        type="button"
      >
        Got it
      </button>
    </div>
  );
}